import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/Eyebrow";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function Guide() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try { const res = await api.get(`/guides/${slug}`); setG(res.data); }
    catch (e) { setErr(e?.response?.status === 404 ? "not_found" : "load_error"); }
    finally { setLoading(false); }
  })(); }, [slug]);

  if (loading) return <Layout><div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-ink-soft">Loading guide…</div></Layout>;
  if (err === "not_found") return <Layout><div className="max-w-3xl mx-auto px-5 sm:px-8 py-20" data-testid="guide-not-found"><Eyebrow>404</Eyebrow><h1 className="font-serif font-medium text-3xl text-ink mt-3 tracking-tight">Guide not found.</h1></div></Layout>;
  if (!g) return null;

  const isGrad = g.category === "graduate_pathways";
  const ctaHref = isGrad ? "/contact?intent=grad" : "/transfer-audit";
  const ctaLabel = isGrad ? "Talk to us about graduate planning — Schedule a Consultation" : "Want this audited against your record? — Schedule a Transfer Audit";
  const bodyLen = (g.body_markdown || "").length;
  const isPlaceholderGuide = !!g.placeholder || bodyLen < 800;

  return (
    <Layout>
      <SEO
        title={g.title}
        description={g.excerpt || (g.body_markdown || "").slice(0, 160)}
        canonical={`/resources/${slug}`}
        ogType="article"
        noindex={isPlaceholderGuide}
        jsonLd={!isPlaceholderGuide ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: g.title,
          datePublished: g.published_at,
          author: { "@type": "Person", name: "Taimur Ahmed" },
          publisher: {
            "@type": "Organization",
            name: "Beyond Valencia",
            logo: { "@type": "ImageObject", url: "https://beyondvalencia.com/logo.png" },
          },
          mainEntityOfPage: `https://beyondvalencia.com/resources/${slug}`,
        } : undefined}
      />
      <article className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10" data-testid="guide-page">
        <div className="lg:col-span-8">
          <Link to="/resources" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-brass hover:text-ivy transition-colors mb-8"><ArrowLeft className="h-3 w-3" /> All guides</Link>
          <div className="text-[10px] uppercase tracking-[0.24em] text-brass">Guide {String(g.number).padStart(2, "0")} · {isGrad ? "Graduate Pathways" : "Florida Transfer"}</div>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[3.5rem] text-ink leading-[1.05] tracking-tightest mt-3">{g.title}</h1>
          {g.excerpt && <p className="mt-6 text-ink-soft text-lg leading-relaxed border-l-2 border-brass pl-5">{g.excerpt}</p>}
          <div className="prose-editorial mt-12" data-testid="guide-body"><ReactMarkdown>{g.body_markdown || ""}</ReactMarkdown></div>
          <div className="mt-12 border-t border-stone/30 pt-8">
            <a href={ctaHref} className="inline-block bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors">{ctaLabel}</a>
          </div>
          {g.number === 16 && <p className="mt-6 text-[11px] uppercase tracking-[0.18em] text-stone">Source: Forbes 2026 New Ivies · Alicia Park · April 8, 2026.</p>}
        </div>
        {g.toc && g.toc.length > 0 && (
          <aside className="lg:col-span-4 hidden lg:block">
            <div className="sticky top-24 border-l border-stone/40 pl-5">
              <div className="text-[10px] uppercase tracking-[0.24em] text-brass mb-4">In this guide</div>
              <ul className="space-y-2.5">
                {g.toc.map((t) => (<li key={t.anchor}><a href={`#${t.anchor}`} className="text-sm text-ink-soft hover:text-ivy transition-colors block">{t.label}</a></li>))}
              </ul>
            </div>
          </aside>
        )}
      </article>
    </Layout>
  );
}
